import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './Privacy.css';

const Privacy = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Privacy Policy - AI Chat Assist</title>
        <meta
          name="description"
          content="Read our privacy policy to understand how AI Chat Assist collects, uses, and protects your personal information."
        />
        <meta
          name="keywords"
          content="privacy policy, data protection, personal information, AI Chat Assist privacy"
        />
        <meta property="og:title" content="Privacy Policy - AI Chat Assist" />
        <meta
          property="og:description"
          content="Read our privacy policy to understand how AI Chat Assist collects, uses, and protects your personal information."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="/privacy" />
        <link rel="canonical" href="/privacy" />
      </Helmet>

      {/* Hero Section */}
      <section className="py-5 bg-light hero-section privacy-page">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <h1 className="display-4 fw-bold mb-4 fade-in">Privacy Policy</h1>
              <p className="lead mb-0 fade-in">Last Updated: May 1, 2025</p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="py-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="card border-0 shadow-sm p-4 p-md-5 fade-in">
                <div className="card-body">
                  <h2 className="fw-bold mb-4">1. Introduction</h2>
                  <p>
                    At AI Chat Assist, we take your privacy seriously. This Privacy Policy explains
                    how we collect, use, disclose, and safeguard your information when you visit our
                    website or use our services.
                  </p>
                  <p>
                    Please read this Privacy Policy carefully. If you do not agree with the terms of
                    this Privacy Policy, please do not access the site or use our services.
                  </p>

                  <h2 className="fw-bold mb-4 mt-5">2. Information We Collect</h2>
                  <p>
                    We may collect information about you in a variety of ways. The information we
                    may collect includes:
                  </p>

                  <h3 className="fw-bold mb-3">2.1 Personal Data</h3>
                  <p>
                    Personally identifiable information, such as your name, email address, telephone
                    number, and demographic information that you voluntarily give to us when you
                    register with us or when you choose to participate in various activities related
                    to our services. You are under no obligation to provide us with personal
                    information of any kind, however, your refusal to do so may prevent you from
                    using certain features of our services.
                  </p>

                  <h3 className="fw-bold mb-3">2.2 Derivative Data</h3>
                  <p>
                    Information our servers automatically collect when you access our services, such
                    as your IP address, browser type, operating system, access times, and the pages
                    you have viewed directly before and after accessing our services.
                  </p>

                  <h3 className="fw-bold mb-3">2.3 Financial Data</h3>
                  <p>
                    Financial information, such as data related to your payment method (e.g., valid
                    credit card number, card brand, expiration date) that we may collect when you
                    purchase, order, return, exchange, or request information about our services. We
                    store only very limited, if any, financial information that we collect.
                    Otherwise, all financial information is stored by our payment processor.
                  </p>

                  <h3 className="fw-bold mb-3">2.4 Data From Social Networks</h3>
                  <p>
                    User information from social networking sites, such as name, your social network
                    username, location, gender, birth date, email address, profile picture, and
                    public data for contacts, if you connect your account to such social networks.
                  </p>

                  <h2 className="fw-bold mb-4 mt-5">3. How We Use Your Information</h2>
                  <p>
                    Having accurate information about you permits us to provide you with a smooth,
                    efficient, and customized experience. Specifically, we may use information
                    collected about you via our services to:
                  </p>
                  <ul>
                    <li>Create and manage your account</li>
                    <li>Process your payments</li>
                    <li>Fulfill and manage your orders</li>
                    <li>Send you administrative information</li>
                    <li>Send you marketing and promotional communications</li>
                    <li>Respond to your inquiries and customer service requests</li>
                    <li>Deliver targeted advertising</li>
                    <li>Administer promotions, sweepstakes, and contests</li>
                    <li>Monitor and analyze usage and trends</li>
                    <li>Improve our services, website, and your experience</li>
                    <li>Protect our services, users, and public</li>
                  </ul>

                  <h2 className="fw-bold mb-4 mt-5">4. Disclosure of Your Information</h2>
                  <p>
                    We may share information we have collected about you in certain situations. Your
                    information may be disclosed as follows:
                  </p>

                  <h3 className="fw-bold mb-3">4.1 By Law or to Protect Rights</h3>
                  <p>
                    If we believe the release of information about you is necessary to respond to
                    legal process, to investigate or remedy potential violations of our policies, or
                    to protect the rights, property, and safety of others, we may share your
                    information as permitted or required by any applicable law, rule, or regulation.
                  </p>

                  <h3 className="fw-bold mb-3">4.2 Third-Party Service Providers</h3>
                  <p>
                    We may share your information with third parties that perform services for us or
                    on our behalf, including payment processing, data analysis, email delivery,
                    hosting services, customer service, and marketing assistance.
                  </p>

                  <h3 className="fw-bold mb-3">4.3 Marketing Communications</h3>
                  <p>
                    With your consent, or with an opportunity for you to withdraw consent, we may
                    share your information with third parties for marketing purposes.
                  </p>

                  <h3 className="fw-bold mb-3">4.4 Business Transfers</h3>
                  <p>
                    If we reorganize or sell all or a portion of our assets, undergo a merger, or
                    are acquired by another entity, we may transfer your information to the
                    successor entity. If we go out of business or enter bankruptcy, your information
                    would be an asset transferred or acquired by a third party.
                  </p>

                  <h2 className="fw-bold mb-4 mt-5">5. Security of Your Information</h2>
                  <p>
                    We use administrative, technical, and physical security measures to help protect
                    your personal information. While we have taken reasonable steps to secure the
                    personal information you provide to us, please be aware that despite our
                    efforts, no security measures are perfect or impenetrable, and no method of data
                    transmission can be guaranteed against any interception or other type of misuse.
                  </p>

                  <h2 className="fw-bold mb-4 mt-5">6. Your Rights Regarding Your Information</h2>
                  <p>
                    You have certain rights regarding the personal information we collect about you.
                    These include:
                  </p>
                  <ul>
                    <li>The right to access and receive a copy of your personal information</li>
                    <li>The right to rectify or update your personal information</li>
                    <li>The right to erase your personal information</li>
                    <li>The right to restrict processing of your personal information</li>
                    <li>The right to object to processing of your personal information</li>
                    <li>The right to data portability</li>
                    <li>The right to withdraw consent</li>
                  </ul>

                  <h2 className="fw-bold mb-4 mt-5">7. California Privacy Rights</h2>
                  <p>
                    California Civil Code Section 1798.83, also known as the "Shine The Light" law,
                    permits our users who are California residents to request and obtain from us,
                    once a year and free of charge, information about categories of personal
                    information (if any) we disclosed to third parties for direct marketing purposes
                    and the names and addresses of all third parties with which we shared personal
                    information in the immediately preceding calendar year.
                  </p>

                  <h2 className="fw-bold mb-4 mt-5">8. Contact Us</h2>
                  <p>
                    If you have questions or comments about this Privacy Policy, please{' '}
                    <Link to="/contact">contact us</Link>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Privacy;
